import { Check, Edit, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button.js';
import { Textarea } from '../../../components/ui/textarea.js';
import {
  useCreateScenarioNote,
  useDeleteScenarioNote,
  useScenarioNotes,
  useUpdateScenarioNote,
  type ScenarioNote,
} from '../notes-hooks.js';
import { formatDateTime } from '../utils.js';

/**
 * Notes panel embedded in the scenario detail modal. Reads notes
 * from the scenario-detail response (the engine does not expose a
 * separate list endpoint), and writes via the per-note POST / PUT /
 * DELETE routes — each mutation invalidates the detail query so the
 * list re-renders without a manual refetch. Dark-themed to match
 * the surrounding modal.
 */
export function ScenarioNotesPanel({ scenarioId }: { scenarioId: string }): JSX.Element {
  const { data, isLoading, error } = useScenarioNotes(scenarioId);
  const { create, isPending: creating, error: createError } = useCreateScenarioNote(scenarioId);
  const { remove, isPending: deleting } = useDeleteScenarioNote(scenarioId);
  const [draft, setDraft] = useState('');

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    try {
      await create(text);
      setDraft('');
    } catch {
      /* surfaces via createError below */
    }
  }

  return (
    <section className="rounded-md border border-foreground/10">
      <header className="border-b border-foreground/10 px-4 py-2 text-sm font-medium uppercase tracking-wider text-foreground/80">
        Notes
      </header>
      <form onSubmit={submit} className="space-y-2 border-b border-foreground/10 p-3">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note for this scenario…"
          rows={2}
          className="border-foreground/15 bg-foreground/5 text-sm text-foreground placeholder:text-foreground/40"
        />
        {createError ? (
          <p className="text-xs text-rose-700 dark:text-rose-300">
            Could not save: {createError.message}
          </p>
        ) : null}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={creating || !draft.trim()}
            className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
          >
            {creating ? 'Saving…' : 'Add note'}
          </Button>
        </div>
      </form>
      <div className="divide-y divide-white/5">
        {isLoading ? (
          <div className="px-4 py-3 text-xs text-foreground/50">Loading…</div>
        ) : error ? (
          <div className="px-4 py-3 text-xs text-rose-700 dark:text-rose-300">{error.message}</div>
        ) : !data || data.length === 0 ? (
          <div className="px-4 py-3 text-xs text-foreground/40">No notes yet</div>
        ) : (
          data.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              scenarioId={scenarioId}
              onDelete={() => void remove(note.id)}
              deleting={deleting}
            />
          ))
        )}
      </div>
    </section>
  );
}

function NoteRow({
  note,
  scenarioId,
  onDelete,
  deleting,
}: {
  note: ScenarioNote;
  scenarioId: string;
  onDelete: () => void;
  deleting: boolean;
}): JSX.Element {
  const { save, isPending: saving, error: saveError } = useUpdateScenarioNote(scenarioId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.noteText);

  const ts = note.modifiedAt || note.createdAt;
  const author = note.modifiedBy || note.createdBy;

  async function commit(): Promise<void> {
    const next = draft.trim();
    if (next === note.noteText.trim()) {
      setEditing(false);
      return;
    }
    try {
      await save(note.id, next);
      setEditing(false);
    } catch {
      /* surfaces via saveError below */
    }
  }

  return (
    <div className="flex gap-3 px-4 py-2.5 text-sm">
      <div className="grow">
        {editing ? (
          <>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              autoFocus
              className="border-foreground/15 bg-foreground/5 text-sm text-foreground"
            />
            {saveError ? (
              <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">{saveError.message}</p>
            ) : null}
            <div className="mt-2 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setDraft(note.noteText);
                  setEditing(false);
                }}
                className="border-foreground/15 text-foreground/70"
              >
                <X className="mr-1 h-3.5 w-3.5" /> Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void commit()}
                disabled={saving}
                className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="whitespace-pre-wrap text-foreground/85">{note.noteText}</p>
            <p className="mt-1 text-xs text-foreground/40">
              {author || 'unknown'}
              {ts ? ` · ${formatDateTime(ts)}` : ''}
              {note.modifiedAt && note.modifiedAt !== note.createdAt ? ' · edited' : ''}
            </p>
          </>
        )}
      </div>
      {!editing ? (
        <div className="flex shrink-0 items-start gap-1.5">
          <button
            type="button"
            onClick={() => {
              setDraft(note.noteText);
              setEditing(true);
            }}
            className="text-foreground/40 transition-colors hover:text-foreground/80"
            aria-label="Edit note"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="text-foreground/40 transition-colors hover:text-rose-700 dark:hover:text-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Delete note"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
